/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { bindActionCreators, compose } from 'redux';
import {
    object, shape, string, func, bool,
} from 'prop-types';
import { connect, useSelector } from 'react-redux';
import mbpLogger from 'mbp-logger';
import Button from '@material-ui/core/Button';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { format } from 'date-fns';

// Material ui
import withStyles from '@material-ui/core/styles/withStyles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';

// redux and helpers
import { resetGiftMessageForm, handleChange } from '../../../../../../state/ducks/Checkout/ducks/Recipient/ducks/GiftMessageForm/GiftMessageForm-Actions';
import TypeChecking from '../../../../../pages/Checkout/containers/CheckoutFloral/Partials/TextFieldWrapper/TypeChecking';
import memberDucks from '../../../../../../state/ducks/Member/ducks';
import {
    getSavedAddress,
    getDataDictionary,
    getUserSubmittedProductFilterZipcode,
} from '../../../../../../state/ducks/App/App-Selectors';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

// Components UI
import ProductPlantsCalendar from './ProductPlantsCalendar';
import GraphqlPortalLoader from '../../../GraphqlCommonComponents/GraphqlPortalLoader';
import getDeliveryCalendarByProduct from '../../../../../../apis/product-apis/getDeliveryCalendarByProduct';

import ErrorMessage from './ErrorMessage';

const styles = (theme) => ({
    root: {
        width: '100%',
        color: '#000',
        background: theme.palette.white,
        position: 'relative',
        marginBottom: '20px',
    },
    checkbox: {
        color: theme.palette.common.black,
        padding: '0px 3px',
        '&$checked': {
            color: theme.palette.primary,
        },
        '& svg': {
            fontSize: '24px',
        },
    },
    formCheckBoxLabel: {
        lineHeight: '16px',
        color: theme.palette?.common.black,
        fontSize: '.6875rem',
        marginTop: 2,
    },
    checked: {
        color: theme.palette.primary,
        '&$checked': {
            color: theme.palette.primary,
        },
    },
    messageHeader: {
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        margin: '10px 0',
        position: 'absolute',
        right: 0,
        top: '5px',
    },
    paperBody: {
        boxSizing: 'border-box',
        padding: '10px 10px 12px 10px',
        border: `1px solid ${theme.palette?.pdp?.cardBorder}`,
        boxShadow: 'none',
        borderRadius: '3px',
    },
    textField: {
        width: '100%',
        height: '106px',
        border: 'none',
        '& label': {
            color: theme.palette?.pdp?.textFieldColor,
            bottom: 0,
            right: 0,
            top: 'auto',
            left: 'auto',
            transformOrigin: 'top right',
            [theme.breakpoints.down('600')]: {
                transformOrigin: 'bottom left',
                bottom: 0,
                left: '-8px',
            },
            '&+ div': {
                marginTop: 0,
                '&:before, &:after': {
                    border: 'none !important',
                },
                '& textarea': {
                    overflow: 'hidden',
                },
            },
        },
        '& label[class*="focused"]': {
            color: theme.palette.primary?.main,
        },
    },
    displayNone: {
        display: 'none',
    },
    displayBlock: {
        display: 'block',
        marginBottom: '30px',
        [theme.breakpoints.down('600')]: {
            marginBottom: '40px',
        },
    },

    // Suggested Messages
    suggestedMessagesLink: {
        fontSize: '12px',
        color: theme.palette.colorNeutral70,
        textDecoration: 'underline',
        cursor: 'pointer',
        display: 'none',
    },

    // Suggested Messages Dialog
    dialogPaperWidth: {
        maxWidth: '800px',
    },
    dialogWrapper: {
        display: 'flex',
        position: 'absolute',
        top: '0',
        height: '400px',
        transition: 'all 0.2s ease-in-out 0s',
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: theme.palette.colorPrimary,
        borderBottom: `2px solid ${theme.palette.colorPrimary}`,
        '& h3': {
            margin: '2px 0',
        },
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        outline: 0,
    },
    dateContainer: {
        margin: '20px 0px',
        '& span': {
            fontSize: '.8125rem',
            marginRight: 10,
        },
        '& input': {
            cursor: 'pointer',
            fontSize: '1rem',
            height: 32,
            borderRadius: '5px',
            border: '1pt solid #000',
        },
    },
    disableDate: {
        opacity: '0.5',
        cursor: 'default',
    },
    errorMessage: {
        margin: 0,
        lineHeight: 1,
        color: 'red',
        marginLeft: '-5px',
    },
    giftMessageOptionalLabel: {
        fontSize: '.8125rem',
        fontWeight: 400,
        marginTop: '0',
        marginBottom: '0.25rem',
    },
    datePickerButton: {
        border: '1pt solid',
        '& span': {
            fontSize: 16,
        },
        '& svg': {
            fontSize: 22,
        },
    },
});

const {
    common: {
        commonSelectors: {
            getAccessToken,
        },
    },
} = memberDucks;
/* eslint-disable react/jsx-props-no-spreading */
const ProductGiftMessage = (props) => {
    const [textField, setTextField] = useState('');
    const [characterCount, setCharacterCount] = useState(0);
    const [maxCharacters, setMaxCharacters] = useState(200);
    const [showGiftMessage, setShowGiftMessage] = useState(false);
    const [fireCalendarQuery, setFireCalendarQuery] = useState(false);
    const [calendarIsLoading, setCalendarIsLoading] = useState(false);
    const [checkBoxClicked, setCheckBoxClicked] = useState(false);
    const {
        classes, dataDictionary, product, isPassportBundleProduct, selectedSku, hasWine, deliveryDate,
        setDeliveryDate, selectedState, stateEnabled,
    } = props;

    useEffect(() => {
        setShowGiftMessage(false);
    }, [selectedSku]);

    /**
     * @description sets default message for sci channel
     */
    const addDefaultMessage = () => {
        const { savedAddress } = props;
        const decedentName = savedAddress?.addressComponents?.DecedentFullName || null;
        let message = '';

        if (decedentName) {
            message = `In loving memory of ${decedentName}.`;
            setTextField(message);

            // callHandleChange({ name: 'message', value: message, '' });
        }
    };

    useEffect(() => {
        setMaxCharacters(200);
        const { callResetGiftMessageForm, savedAddress: { addressSource } } = props;
        callResetGiftMessageForm();

        if (addressSource === 'SCI' && !textField) {
            addDefaultMessage();
        }
    }, []);
    const validatedZip = useSelector(getUserSubmittedProductFilterZipcode);
    const getCalendarFromAPI = () => {
        const {
            locationType,
            jwt,
        } = props;
        // Starting at the end of the string (backwards pattern matching), continue to match any characters that aren't '-' or '_'
        const itemSku = selectedSku.id.match(/[^-_]*$/);
        const brandId = selectedSku.brandId;

        getDeliveryCalendarByProduct(
            {},
            jwt,
            itemSku,
            brandId,
            validatedZip || 35005,
            locationType || 'Residence',
            '',
        )
            .then(({ data }) => {
                setCalendarIsLoading(false);

                if (data === '' || data.response?.calendarDates.length === 0) {
                    setDeliveryDate(false);
                    return;
                }
                const noSurchargeDate = data.response?.calendarDates.find(({ upcharge }) => upcharge === '0.0');
                const standardDeliveryDate = (noSurchargeDate && noSurchargeDate) || data.response?.calendarDates[0];
                setDeliveryDate({ data: standardDeliveryDate });
            })
            .catch((err) => {
                setCalendarIsLoading(false);
                mbpLogger.logError({
                    component: 'GraphqlPDPCalendar',
                    apiCall: 'getDeliveryCalendarByProduct',
                    jsError: err,
                    jwt,
                    itemSku,
                    brandId,
                    validatedZip,
                    locationType,
                    startDate: '',
                });
            });
    };

    const handleChanges = (event) => {
        const { callHandleChange } = props;
        let resultValue = event.target.value;
        if (resultValue.length <= maxCharacters) {
            // Review the restriction in the input
            const dataToCheck = { value: event.target.value };
            const restrictInput = TypeChecking.MultilineGiftMessage;
            if (restrictInput) {
                resultValue = (restrictInput) ? restrictInput(dataToCheck) : dataToCheck.value;
            }

            callHandleChange({ name: 'message', value: resultValue });
            setTextField(resultValue);
            setCharacterCount(resultValue.length);
        }
    };

    const handleShowGiftMessage = () => {
        setCheckBoxClicked(true);
        if (stateEnabled && !selectedState) return null;
        props.tagManagerActions({
            eventName: 'feature_interaction',
            eventCategory: 'Product Page',
            eventAction: 'Click',
            eventLabel: 'This is a Gift',
        });
        setShowGiftMessage(!showGiftMessage);
        getCalendarFromAPI();
        return setCalendarIsLoading(true);
    };

    const handleCalendarClick = () => {
        setFireCalendarQuery(true);
        setCalendarIsLoading(true);
    };

    const productPlantsCalendarProps = {
        product,
        productId: product.productId,
        isPassportBundleProduct,
        selectedSku,
        hasWine,
        fireCalendarQuery,
        setFireCalendarQuery,
        setCalendarIsLoading,
        calendarIsLoading,
        deliveryDate,
        setDeliveryDate,
        validatedZip,
    };
    return (
        <div className={classes.root}>
            {calendarIsLoading && <GraphqlPortalLoader />}
            <FormControlLabel
                label={<strong>THIS IS A GIFT</strong>}
                classes={{ label: ` ${classes.formCheckBoxLabel} ${(stateEnabled && !selectedState) ? classes.disableDate : null}` }}
                control={(
                    <Checkbox
                        id="optional-messsage"
                        data-test="pdp-optional-message"
                        data-testid="pdp-optional-message"
                        name="pdpMessage"
                        disableRipple
                        value="checkedB"
                        color="primary"
                        checked={showGiftMessage}
                        onChange={handleShowGiftMessage}
                        error
                        classes={{
                            root: `${classes.checkbox} ${(stateEnabled && !selectedState) ? classes.disableDate : null}`,
                            checked: classes.checked,
                        }}
                    />
                )}
            />
            {(stateEnabled && !selectedState && checkBoxClicked) && <FormHelperText className={classes.errorMessage}>{ErrorMessage['stateValidtion']}</FormHelperText>}
            {showGiftMessage ? (
                <div
                    className={
                        showGiftMessage
                            ? classes.displayBlock
                            : classes.displayNone
                    }
                >
                    {deliveryDate && (
                        <div className={classes.dateContainer}>
                            <span>Enter Delivery Date<sup>*</sup></span>
                            <Button
                                className={classes.datePickerButton}
                                aria-label=""
                                onClick={handleCalendarClick}
                            >   <span>{format(new Date(deliveryDate?.data?.deliveryDate.replaceAll('-', '/')), 'MM / dd / yyyy')}</span>
                                <CalendarTodayIcon />
                            </Button>
                        </div>
                    )}
                    {fireCalendarQuery && <ProductPlantsCalendar {...productPlantsCalendarProps} />}
                    <p className={classes.giftMessageOptionalLabel}>{dataDictionary?.['Add a Card Message (optional)'] || 'Add a Card Message (optional)'} : </p>
                    <div>
                        <Paper className={classes.paperBody} elevation={1}>
                            <TextField
                                id="filled-multiline-static"
                                data-test="pdp-filled-multiline-static"
                                data-testid="pdp-filled-multiline-static"
                                name="giftMessage"
                                label={`${maxCharacters
                                    - characterCount} ${dataDictionary?.['Characters Left'] || 'Characters Left'}`}
                                multiline
                                rows="4"
                                value={textField}
                                onChange={handleChanges}
                                className={classes.textField}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Paper>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    callResetGiftMessageForm: bindActionCreators(resetGiftMessageForm, dispatch),
    callHandleChange: bindActionCreators(handleChange, dispatch),
    tagManagerActions: bindActionCreators(trackEvent, dispatch),
});

const mapStateToProps = (state) => ({
    savedAddress: getSavedAddress(state),
    dataDictionary: getDataDictionary(state),
    jwt: getAccessToken(state),
});

ProductGiftMessage.defaultProps = {
    savedAddress: {
        addressComponents: {
            DecedentFullName: '',
        },
        addressSource: '',
    },
};

ProductGiftMessage.propTypes = {
    classes: object.isRequired,
    callResetGiftMessageForm: func.isRequired,
    callHandleChange: func.isRequired,
    savedAddress: shape({
        addressComponents: shape({
            DecedentFullName: string,
        }),
        addressSource: string,
    }),
    dataDictionary: object.isRequired,
    product: object.isRequired,
    isPassportBundleProduct: bool.isRequired,
    selectedSku: string.isRequired,
    hasWine: bool.isRequired,
    deliveryDate: string.isRequired,
    setDeliveryDate: func.isRequired,
    selectedState: string.isRequired,
    locationType: string.isRequired,
    jwt: string.isRequired,
    stateEnabled: bool.isRequired,
    tagManagerActions: func.isRequired,
};

export default compose(
    withStyles(styles),
    connect(mapStateToProps, mapDispatchToProps),
)(ProductGiftMessage);
